import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_CURRENCY_CODE,
  formatCurrency,
  getPackPriceDisplay,
} from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { LabelConfig, useRenderedLabels } from '@/hooks/use-item-labels';
import { MultiStyleText } from './multi-style-text';
import { PackUnit } from '@/enums';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { useMaybePaymentsContext } from '@/contexts/payments-context';

interface ItemSubtitleV2Props {
  baseUnit?: PackUnit | null;
  currencyCode: string | null;
  displayString: string;
  heading?: React.ReactNode;
  isCompactView?: boolean;
  labels?: LabelConfig[];
  modifierString?: string;
  promotionPriceInMicros?: number | null;
  quantityInStock?: number;
  sku?: string | null;
  style?: StyleProp<ViewStyle>;
  unitCount: number;
  unitPriceInMicros?: number | null;
}

const ItemSubtitleV2Inner: React.FC<ItemSubtitleV2Props> = ({
  baseUnit,
  currencyCode,
  displayString,
  heading,
  isCompactView,
  labels,
  modifierString,
  promotionPriceInMicros,
  quantityInStock,
  sku,
  style,
  unitCount,
  unitPriceInMicros,
}) => {
  const [styles] = useStyles();
  const paymentsContext = useMaybePaymentsContext();
  const defaultCurrencyCode =
    paymentsContext?.currencyCode || DEFAULT_CURRENCY_CODE;
  const finalCurrencyCode = currencyCode || defaultCurrencyCode;

  const inStockCopy =
    typeof quantityInStock === 'number'
      ? i18n.t('in_stock', { quantity: quantityInStock })
      : null;

  const bottomRowText = useMemo(() => {
    const {
      formattedPackPrice,
      formattedPromotionPackPrice,
      isPackPriceDiscounted,
      perUnitPriceCopy,
    } = getPackPriceDisplay({
      baseUnit,
      currencyCode: finalCurrencyCode,
      promotionPricePerUnitInMicros: promotionPriceInMicros,
      unitCount,
      unitPriceInMicros,
    });

    const bottomRowComponents: React.ReactNode[] = [];
    if (formattedPromotionPackPrice) {
      bottomRowComponents.push(
        <Text key="promotion-price" style={FONTS.primaryBold}>
          {formattedPromotionPackPrice}
        </Text>,
      );
    }

    if (formattedPackPrice) {
      bottomRowComponents.push(
        <Text
          key="unit-price"
          style={
            isPackPriceDiscounted ? styles.strikethroughText : FONTS.primaryBold
          }
        >
          {formattedPackPrice}
        </Text>,
      );
    }

    if (perUnitPriceCopy) {
      bottomRowComponents.push(<Text key="per-unit-space">&nbsp;</Text>);
      bottomRowComponents.push(
        <MultiStyleText
          defaultStyle={[TYPOGRAPHY.listSmall, styles.perUnitText]}
          emphasisStyle={[TYPOGRAPHY.listSmall, styles.perUnitStrikethrough]}
          key="per-unit-price"
        >
          {perUnitPriceCopy}
        </MultiStyleText>,
      );
    }

    return bottomRowComponents.length > 0 ? (
      <Text style={[TYPOGRAPHY.listSubtitle, styles.priceText]}>
        {bottomRowComponents.reduce<React.ReactNode[]>(
          (res, component) =>
            res.length ? [res, ' ', component] : [component],
          [],
        )}
      </Text>
    ) : null;
  }, [
    baseUnit,
    finalCurrencyCode,
    promotionPriceInMicros,
    styles,
    unitCount,
    unitPriceInMicros,
  ]);

  const renderedLabels = useRenderedLabels(labels);

  const bottomRow =
    renderedLabels?.length || bottomRowText ? (
      <View style={[styles.priceRow, style]}>
        {bottomRowText}
        {renderedLabels}
      </View>
    ) : null;

  const priceString = formatCurrency(unitPriceInMicros || 0, finalCurrencyCode);
  const unitPriceString =
    priceString || displayString
      ? [priceString, displayString].join('/')
      : null;

  const topRowContentList = isCompactView
    ? [
        sku ? <Text style={styles.uppercaseText}>{sku}</Text> : null,
        unitPriceString ? (
          <Text style={styles.capitalizedText}>{unitPriceString}</Text>
        ) : null,
        baseUnit ? (
          <Text style={styles.capitalizedText}>
            {LOCALIZED_PACK_UNITS[baseUnit]}
          </Text>
        ) : null,
      ]
    : [
        displayString ? (
          <Text style={styles.capitalizedText}>{displayString}</Text>
        ) : null,
        sku ? <Text style={styles.uppercaseText}>{sku}</Text> : null,
        inStockCopy ? (
          <Text style={[TYPOGRAPHY.listSmall, styles.inStock]}>
            {inStockCopy}
          </Text>
        ) : null,
      ];

  return (
    <View style={[styles.base, style]}>
      {heading ? heading : null}
      <View style={styles.topRowContainer}>
        <Text style={[styles.textContainer, styles.displayText]}>
          {topRowContentList.map((component, index) => (
            <React.Fragment key={index}>
              {index > 0 && component ? ' • ' : ''}
              {component}
            </React.Fragment>
          ))}
        </Text>
        {isCompactView && renderedLabels ? renderedLabels : null}
      </View>
      {modifierString ? (
        <Text
          numberOfLines={1}
          style={[TYPOGRAPHY.listSmall, styles.displayText]}
        >
          {modifierString}
        </Text>
      ) : null}
      {isCompactView ? null : bottomRow}
    </View>
  );
};

// This leaf renders purely from primitive props + a stable style, but it's
// mounted dozens of times across item lists, so it pays for every
// parent-driven cascade. memo lets it bail out when its own props haven't
// changed.
export const ItemSubtitleV2 = React.memo(ItemSubtitleV2Inner);

const useStyles = createStyles((theme) => ({
  base: {
    alignSelf: 'stretch',
    flexGrow: 1,
    justifyContent: 'center',
    width: '100%',
  },
  capitalizedText: {
    textTransform: 'capitalize',
  },
  displayText: {
    color: theme.colors.secondaryText,
    fontFamily: theme.fonts.primary,
    fontSize: theme.fontSizes.small,
    lineHeight: 18,
  },
  inStock: {
    ...FONTS.primaryBold,
    color: theme.colors.darkGreen,
  },
  perUnitStrikethrough: {
    color: theme.colors.secondaryText,
    textDecorationLine: 'line-through',
    textTransform: 'lowercase',
  },
  perUnitText: {
    color: theme.colors.secondaryText,
    textTransform: 'lowercase',
  },
  priceRow: {
    alignItems: 'center',
    columnGap: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 2,
    rowGap: 2,
    width: '100%',
  },
  priceText: {
    flexShrink: 1,
    textTransform: 'uppercase',
  },
  strikethroughText: {
    textDecorationLine: 'line-through',
  },
  textContainer: {
    flexDirection: 'row',
    gap: 4,
  },
  topRowContainer: {
    flexDirection: 'row',
    flexGrow: 1,
    flexWrap: 'wrap',
    gap: 8,
  },
  uppercaseText: {
    textTransform: 'uppercase',
  },
}));
